import React from 'react';
import { AlertTriangle } from 'lucide-react';
import FormModal from '@shared/ui/FormModal';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
}

const TONE = {
  danger: 'text-error-600 dark:text-error-400',
  warning: 'text-warning-600 dark:text-warning-400',
  info: 'text-accent-600 dark:text-accent-400',
} as const;

/**
 * The app's confirm dialog — 40 files use it, which is why it was worth
 * converting first.
 *
 * The props are unchanged, so no call site moves. What changes is underneath: it
 * now sits on <FormModal>, which brings the focus trap, scroll lock, Escape
 * handling and unique title id it never had. It previously declared
 * aria-labelledby="dialog-title" as a hardcoded string, so two confirms mounted
 * at once pointed at the same element.
 *
 * `variant` now colours only the icon. The confirm button takes Button's `danger`
 * or `primary` rather than a hand-built class string, so a destructive confirm
 * looks like every other destructive action in the app.
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'danger',
}) => (
  <FormModal
    isOpen={isOpen}
    title={title}
    onClose={onCancel}
    onSubmit={onConfirm}
    size="sm"
    submitLabel={confirmText}
    cancelLabel={cancelText}
    submitVariant={variant === 'danger' ? 'danger' : 'primary'}
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className={`mt-0.5 h-6 w-6 shrink-0 ${TONE[variant]}`} aria-hidden="true" />
      <p className="text-text-secondary dark:text-text-dark-secondary">{message}</p>
    </div>
  </FormModal>
);

export default ConfirmationDialog;
